"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  changeAiDisclosureSettingAction,
  saveAiDisclosureTemplateAction,
  syncVoiceDisclosureOpeningsAction,
} from "@/server-actions/ai-disclosure-settings"
import { aiDisclosureSettingsQueryPrefix } from "@/hooks/queries/ai-disclosure-settings"
import { withQueryHandling } from "@/lib/with-query-handling"
import type { AiDisclosureTemplateChannel } from "@/lib/ai-disclosure/settings"

export function useChangeAiDisclosureSettingMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { enabled: boolean; expectedEnabled: boolean; acceptedConfirmationText?: string }) => {
      const result = await withQueryHandling(changeAiDisclosureSettingAction(input), {
        loading: "Updating AI disclosure…",
        success: "AI disclosure updated",
        showErrorToast: false,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: aiDisclosureSettingsQueryPrefix })
      if (result.voiceSyncFailed > 0) {
        toast.warning(`${result.voiceSyncFailed} voice agents could not be updated. Retry synchronization before using them.`)
      }
    },
  })
}

export function useSaveAiDisclosureTemplateMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { channel: AiDisclosureTemplateChannel; language: string; template: string }) => {
      const result = await withQueryHandling(saveAiDisclosureTemplateAction(input), {
        loading: "Saving AI disclosure wording…",
        success: "AI disclosure wording saved",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: aiDisclosureSettingsQueryPrefix })
      if (result.voiceSyncFailed > 0) {
        toast.warning(`${result.voiceSyncFailed} voice agents could not be updated. Retry synchronization before using them.`)
      }
    },
  })
}

export function useSyncVoiceDisclosureOpeningsMutation() {
  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(syncVoiceDisclosureOpeningsAction({}), {
        loading: "Synchronizing voice openings…",
        success: "Voice openings synchronized",
      })
      if ("status" in result) throw new Error(result.errorMessage)
      if (result.failed > 0) {
        toast.warning(`${result.failed} voice agents could not be updated.`)
      }
      return result
    },
  })
}
